import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const SMSSetup = ({ onComplete, onBack, loading }) => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);

  const formatPhoneNumber = (value) => {
    const cleaned = value?.replace(/\D/g, '');
    const match = cleaned?.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);
    if (match) {
      return !match?.[2] ? match?.[1] : `(${match?.[1]}) ${match?.[2]}${match?.[3] ? `-${match?.[3]}` : ''}`;
    }
    return value;
  };

  const handleSendCode = async () => {
    const cleaned = phoneNumber?.replace(/\D/g, '');
    if (cleaned?.length !== 10) {
      setError('Please enter a valid 10-digit phone number');
      return;
    }

    try {
      setSending(true);
      setError('');
      
      // In production, call API to send SMS
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setCodeSent(true);
    } catch (err) {
      setError('Failed to send verification code. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleVerify = () => {
    if (!verificationCode || verificationCode?.length !== 6) {
      setError('Please enter a 6-digit verification code');
      return;
    }

    // In production, verify the code
    if (verificationCode?.match(/^\d{6}$/)) {
      onComplete({ phoneNumber, method: 'sms' });
    } else {
      setError('Invalid verification code format');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-foreground mb-2">Set Up SMS Verification</h2>
        <p className="text-muted-foreground">Enter your phone number to receive verification codes</p>
      </div>

      {/* Security Notice */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Icon name="AlertTriangle" size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-sm font-semibold text-amber-900 mb-1">Security Notice</h3>
            <p className="text-xs text-amber-800">
              SMS is less secure than authenticator apps. Consider using an authenticator app for better security.
            </p>
          </div>
        </div>
      </div>

      {/* Step 1: Enter Phone Number */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold">
            1
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-foreground mb-2">Enter Phone Number</h3>
            <p className="text-sm text-muted-foreground mb-4">
              We'll send a verification code to this number
            </p>
            <div className="space-y-4">
              <Input
                type="tel"
                placeholder="(555) 123-4567"
                value={phoneNumber}
                onChange={(e) => {
                  setPhoneNumber(formatPhoneNumber(e?.target?.value));
                  setError('');
                }}
                disabled={codeSent}
                maxLength={14}
              />
              {!codeSent && (
                <Button
                  onClick={handleSendCode}
                  loading={sending}
                  disabled={phoneNumber?.replace(/\D/g, '')?.length !== 10}
                  iconName="Send"
                  iconPosition="right"
                >
                  Send Verification Code
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Step 2: Enter Verification Code */}
      {codeSent && (
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold">
              2
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-foreground mb-2">Enter Verification Code</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Enter the 6-digit code sent to {phoneNumber}
              </p>
              <div className="space-y-4">
                <Input
                  type="text"
                  placeholder="000000"
                  value={verificationCode}
                  onChange={(e) => {
                    setVerificationCode(e?.target?.value?.replace(/\D/g, '')?.slice(0, 6));
                    setError('');
                  }}
                  error={error}
                  maxLength={6}
                  className="text-center text-2xl tracking-widest font-mono"
                />
                <button
                  onClick={handleSendCode}
                  className="text-sm text-primary hover:underline"
                  disabled={sending}
                >
                  Didn't receive code? Resend
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-3">
        <Button variant="outline" onClick={onBack} disabled={loading}>
          Back
        </Button>
        {codeSent && (
          <Button
            onClick={handleVerify}
            loading={loading}
            disabled={verificationCode?.length !== 6}
            className="flex-1"
            iconName="ArrowRight"
            iconPosition="right"
          >
            Verify and Continue
          </Button>
        )}
      </div>
    </div>
  );
};

export default SMSSetup;